import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useDataFastGoals } from "@/hooks/useDataFastGoals";
import WhatsAppCTA from "./WhatsAppCTA";
import { Link } from "react-router-dom";
import { useSectionViewGoal } from "@/hooks/useSectionViewGoal";
import { ArrowRight } from "lucide-react";
import { SectionHeading } from "./primitives";
import { CONTAINER, SECTION_Y } from "./ui-tokens";

export const items = [
  {
    // A pergunta de definição. Fica fora da home (que já se explica no hero)
    // e abre o /faq, onde é a frase que um buscador ou assistente cita quando
    // alguém pergunta "o que é o Ecclesiato". Mesma frase do llms.txt.
    id: "0",
    title: "O que é o Ecclesiato?",
    content:
      "É um sistema de gestão para igrejas, brasileiro e em português, que roda no navegador. Reúne cadastro de membros e visitantes, células, ministério infantil com check-in por QR Code, escala de voluntários gerada por regras, eventos com inscrição por Pix e controle financeiro de dízimos e ofertas. Atende a igreja local, com planos de R$ 97 a R$ 1.197 por mês até 3.000 pessoas ativas, plano sob medida acima disso e os mais de 20 módulos liberados em todos eles. Foi criado por Vinicius Graciano dentro da IEQ Central de Piracicaba (SP), que usa o sistema no dia a dia.",
  },
  {
    id: "1",
    title: "O Ecclesiato faz migração de dados de outros sistemas?",
    content:
      "Sim, e a migração é feita pela nossa equipe. Você manda a planilha ou a exportação do sistema atual do jeito que ela está, sem reorganizar coluna nem padronizar data. Começamos por um lote piloto de 50 a 100 pessoas para a igreja conferir e aprovar, e só então trazemos a base inteira — com ministérios, batismo, data de filiação e endereço junto. O histórico não fica para trás e nada disso é cobrado.",
  },
  {
    id: "1.5",
    title: "Vocês cobram taxa de implantação ou adesão?",
    content:
      "Não. A transição, a migração dos dados e a configuração inicial são por nossa conta, e isso não é promoção de entrada: não existe taxa de adesão nem de implantação em nenhum plano.",
  },
  {
    id: "1.6",
    title: "Além da mensalidade, o que mais a igreja vai pagar?",
    content:
      "Nada. O valor do plano é o custo total: implantação, migração das planilhas, treinamento da equipe, suporte, usuários adicionais, os mais de 20 módulos e as atualizações estão inclusos. Não cobramos por volume de cadastros importados, por relatório, por armazenamento nem taxa de adesão. Você paga mais só se a igreja crescer e precisar subir de plano — e nesse caso a diferença é a do próprio plano, sem taxa de upgrade.",
  },
  {
    // Esta pergunta existe para deixar explícito que o cartão é pedido no
    // cadastro mesmo sem cobrança imediata. Quem descobre isso só no checkout
    // desiste ali, e com razão — a informação tinha que estar antes.
    id: "1.7",
    title: "Existe período de teste grátis?",
    content:
      "Existe: 7 dias grátis, com os mais de 20 módulos. Cinco funções que entregam tudo dentro da própria semana só liberam na primeira cobrança: pagamento online de inscrições, carteirinha de membro, ficha de membro, certificado de treinamento e votação pública. Você escolhe o plano e cadastra o cartão, mas nada é cobrado agora — a primeira cobrança acontece só no 8º dia, e avisamos por e-mail no dia anterior. Cancele antes disso e nada é cobrado. Durante o teste a igreja usa os mais de 20 módulos, com o suporte e a migração das planilhas feita pela nossa equipe: você avalia o sistema já implantado com a sua base dentro, não uma conta vazia. E depois da primeira cobrança ainda valem os 7 dias de garantia — se concluir que não era para vocês, devolvemos 100% do valor, desde que a igreja não tenha usado, depois da cobrança, uma das cinco funções que liberam nela.",
  },
  {
    // A objeção real não é o valor, é o critério. Quem pergunta isso está
    // desconfiado de que a conta vai inflar sozinha quando a igreja crescer —
    // então a resposta precisa dizer o que NÃO conta para o limite.
    id: "1.8",
    title: "Por que o preço é por número de membros, e não por módulo?",
    content:
      "Porque é o único critério que acompanha o tamanho real da operação sem transformar a escolha do plano num quebra-cabeça. Os mais de 20 módulos estão liberados em todos os planos: uma igreja de 90 pessoas usa exatamente o mesmo sistema que uma de 1.500, e nenhuma função fica atrás de uma proposta comercial. O limite conta apenas pessoas ativas — visitante (inclusive a criança visitante do Kids) e cadastro inativo não entram na conta; a criança cadastrada como membro conta — e ministérios, células, eventos, usuários do sistema e contas bancárias são ilimitados em qualquer faixa.",
  },
  {
    // Aparece em toda comparação com outros sistemas, e a resposta honesta é
    // "não temos". Esconder isso só transfere a descoberta para depois da
    // assinatura, que é o pior momento possível.
    id: "1.9",
    title: "O Ecclesiato tem aplicativo próprio na App Store e na Google Play?",
    content:
      "Não, e é uma escolha de produto. O membro acessa tudo por links públicos que abrem no navegador, sem download e sem senha: na Central do Membro ele atualiza os próprios dados, vê as escalas em que está, consulta a agenda, se inscreve em eventos, envia pedido de oração e abre a carteirinha das crianças com QR Code para o check-in do infantil. O mesmo vale para indisponibilidade, presença em escala, frequência de célula e recepção de visitante — o visitante de domingo não baixa aplicativo para preencher uma ficha. Para a equipe que usa o sistema todo dia, ele se instala como atalho na tela inicial do celular. O que não existe: notificação push, mural interno de avisos e transmissão de culto. Se um app com o nome da sua igreja nas lojas é requisito fechado, nós não atendemos isso, e dizemos quais sistemas atendem nos nossos comparativos.",
  },
  {
    id: "2",
    title: "É possível personalizar os relatórios e documentos?",
    content:
      "Em boa parte, sim. Além dos relatórios padrão de membros e finanças, personalizamos ficha de cadastro e carteirinha para a realidade da sua igreja — é só pedir pelo suporte. Relatório muito fora do padrão a gente avalia caso a caso e diz se dá, em vez de prometer antes de olhar.",
  },
  {
    id: "3",
    title: "O Ecclesiato é seguro e compatível com LGPD?",
    content:
      "Sim. O sistema roda em infraestrutura Google Cloud/Firebase, com criptografia em trânsito e em repouso, backup automático diário e permissão de acesso por papel — cada líder enxerga apenas o que é dele. As alterações em pessoas, finanças, escalas e eventos ficam registradas em log de auditoria, com quem fez, o que mudou e quando. O tratamento dos dados segue as diretrizes da LGPD e a exclusão da base pode ser solicitada pela igreja a qualquer momento.",
  },
  {
    id: "3.5",
    title: "Se eu cancelar, consigo levar todos os meus dados comigo?",
    content:
      "Sim, e essa é uma pergunta que você deveria fazer a qualquer sistema antes de contratar. Você exporta a lista de pessoas, os relatórios e a exportação contábil em CSV ou Excel — o formato que o seu contador já usa — direto do sistema, sem depender da nossa equipe e sem taxa. Seus dados são da sua igreja: não usamos a base como forma de prender ninguém.",
  },
  {
    id: "3.6",
    title: "Como funciona o ministério infantil? Dá conta de um culto grande?",
    content:
      "O fluxo completo está no sistema: a criança tem ficha própria com faixa etária, sala, restrições médicas e medicamentos autorizados, além da lista de pessoas autorizadas a retirá-la. O check-in é feito por QR Code — e você pode operar de vários dispositivos ao mesmo tempo, com tablets ou celulares da equipe, o que evita fila num culto de centenas de pessoas. Na saída, o sistema registra o check-out e a retirada só é liberada para quem está autorizado na ficha. Também há histórico de frequência por criança e por sala.",
  },
  {
    id: "3.7",
    title: "E quando um voluntário escalado não pode servir?",
    content:
      "O Ecclesiato resolve isso antes da escala existir, e é importante ser claro sobre como: cada voluntário preenche um link informando os dias em que não pode servir e o culto que prefere, e o líder define regras como limite de escalas por pessoa no mês, intervalo mínimo entre escalas, quem serve junto e pelo menos um veterano por culto. A escala é gerada automaticamente respeitando tudo isso e cruzando os outros ministérios, para ninguém ficar escalado em dois lugares no mesmo culto, o que reduz muito o número de trocas de última hora. Se ainda assim alguém faltar, a página de presença sugere quem chamar no lugar. Depois do culto, o responsável registra ali quem esteve, quem trocou e quem faltou, e isso fica no relatório de presença em escalas. O que o sistema não faz hoje é o voluntário aceitar ou recusar a escala dentro dele — ele visualiza a escala pelo link público, pelo Google Agenda ou pelo calendário do iPhone.",
  },
  {
    id: "4",
    title: "Como funciona o suporte técnico?",
    content:
      "Você fala direto com o Vinicius, que desenvolve o Ecclesiato, pelo WhatsApp e por e-mail. Sem robô e sem fila de chamado: a dúvida chega em quem escreveu o sistema.",
  },
  {
    id: "5",
    title: "O sistema serve para igrejas pequenas ou em plantação?",
    content:
      "Serve, e é onde o plano Essencial faz sentido: até 150 pessoas ativas, os mais de 20 módulos liberados e a mesma implantação que uma igreja grande recebe. Congregação em plantação costuma ganhar mais rápido nas duas primeiras semanas, porque a base ainda é pequena e entra inteira de uma vez.",
  },
  {
    id: "6",
    title: "E se minha igreja crescer além do limite do plano?",
    content:
      "O limite considera apenas pessoas ativas: membros e frequentadores com cadastro ativo. Visitantes (inclusive a criança visitante do Kids) e cadastros inativos não entram na conta; a criança cadastrada como membro conta, e ministérios, células e eventos são ilimitados em todos os planos. Ao atingir o limite, o upgrade é um clique, sem perda de dado e sem interrupção — e a diferença cobrada é a do próprio plano, sem taxa de upgrade. Acima de 3.000 pessoas ativas, o plano é sob medida: cota e preço combinados com a equipe pelo WhatsApp.",
  },
  {
    id: "7",
    title: "Preciso de conhecimento técnico para usar o sistema?",
    content:
      "Não. Focamos em criar uma interface extremamente limpa e intuitiva. Se você já usa redes sociais ou aplicativos de banco, conseguirá dominar o Ecclesiato em poucos minutos. Além disso, nossa equipe de suporte está sempre à disposição.",
  },
  {
    id: "8",
    title: "Posso cancelar minha assinatura quando quiser?",
    content:
      "Sim. Não acreditamos em contratos de fidelidade que prendem o usuário. Você pode cancelar sua assinatura a qualquer momento, sem taxas ocultas ou burocracia. E o risco é praticamente nenhum: são 7 dias grátis antes da primeira cobrança, e mais 7 dias de garantia integral depois dela.",
  },
];

/**
 * As perguntas que a HOME renderiza — sete, não dezesseis.
 *
 * Uma FAQ de landing não é central de ajuda: ela existe para matar as objeções
 * que travam a assinatura, e cada pergunta a mais é rolagem entre o visitante e
 * o botão. Estas sete cobrem, nesta ordem, o que trava a decisão: o cartão no
 * teste, o custo total, a base histórica, a adoção da equipe, LGPD, a saída com
 * os dados e o cancelamento.
 *
 * O resto continua publicado — `items` inteiro alimenta /faq, que é a central
 * de ajuda de verdade e carrega o FAQPage do schema com as dezesseis. A home
 * declara no JSON-LD só as sete que ela mostra: dado estruturado precisa bater
 * com o conteúdo visível da página.
 *
 * As que saíram daqui de propósito e por quê: 1.5 (taxa de implantação) é dita
 * por 1.6, que responde a pergunta inteira; 1.8 (preço por membro) e 1.9 (app
 * nas lojas) são de quem compara produto nomeado — o lugar delas é /comparar.
 */
const HOME_FAQ_IDS = ["1.7", "1.6", "1", "7", "3", "3.5", "8"];

export const homeItems = HOME_FAQ_IDS.map(
  (id) => items.find((item) => item.id === id)!,
);

export default function Faq() {
  const { trackWhatsappClicked, trackFaqExpanded } = useDataFastGoals();
  const ref = useSectionViewGoal("faq");

  return (
    <section
      ref={ref}
      id="faq"
      aria-labelledby="faq-titulo"
      className={`${SECTION_Y} bg-secondary/40`}
    >
      <div className={`${CONTAINER} grid gap-12 lg:grid-cols-12 lg:gap-16`}>
        {/* O título e o contato ficam presos à esquerda enquanto a lista rola:
            quem não achou a resposta tem a saída sempre à vista. */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <SectionHeading
              id="faq-titulo"
              title="Antes de decidir,"
              muted="as perguntas que todo pastor faz."
            >
              Teste, custo, migração, segurança e cancelamento, respondidos sem
              rodeio.
            </SectionHeading>

            {/* Aqui havia um formulário de quatro campos colado num botão de
                WhatsApp que resolve a mesma coisa em um toque. Num público que
                já vive no WhatsApp, o formulário era o caminho pior
                competindo com o melhor. O canal por escrito continua: o
                e-mail está no rodapé, e /faq tem a central inteira. */}
            <div className="lp-reveal mt-10">
              <p className="font-semibold text-foreground">
                Ficou uma dúvida que não está aqui?
              </p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                Fale com uma pessoa de verdade. Quem responde é o Vinicius, que
                construiu o sistema e faz a implantação.
              </p>
              <WhatsAppCTA
                variant="outline"
                text="Tirar a dúvida no WhatsApp"
                customMessage="Olá! Tenho uma dúvida sobre o Ecclesiato e gostaria de conversar com a equipe."
                onClick={() => trackWhatsappClicked("faq_section")}
                className="mt-4"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Accordion
            type="multiple"
            className="lp-reveal lp-surface w-full px-6"
          >
            {homeItems.map((item) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                className="border-b border-border last:border-b-0"
              >
                <AccordionTrigger
                  className="py-5 text-left text-base font-semibold text-foreground hover:no-underline"
                  onClick={() => trackFaqExpanded(item.id)}
                >
                  {item.title}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-pretty text-muted-foreground">
                  {item.content}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* A central completa, para quem quer ler o resto sem que as
              dezesseis perguntas atravessem o caminho de quem só quer
              assinar. */}
          <Link
            to="/faq"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline hover:underline-offset-4"
          >
            Ver todas as {items.length} perguntas na central de ajuda
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
